/**
 * Campaign rival & player PORTRAIT resolution.
 *
 * Campaign rivals are written as HUMAN TRAINERS (Youngster, Blackbelt,
 * Channeler…), so their portrait should be a trainer illustration — NOT the
 * Suikamon creature that leads their squad. This module is the single source of
 * truth for that mapping: a rival with bespoke trainer art in
 * `/public/campaign/trainers/` uses it; every other rival (the one-off / abstract
 * story bosses that don't have art yet) FALLS BACK to the stage-accurate species
 * image, exactly as before, so nothing 404s.
 *
 * Adding art for a new rival is a two-step, data-only change (see
 * docs/superpowers/specs/2026-07-10-campaign-trainer-portraits.md):
 *   1. drop `<rivalId>.png` (transparent, full-body) into /public/campaign/trainers/
 *   2. add that rivalId to TRAINER_PORTRAIT_IDS below
 * where rivalId is the slug of the rival's name (see rivalIdForName).
 */

import { rivalIdForName, type CampaignNode } from '@shared/domain/campaignConfig';
import { CAMPAIGN_CAST_PROFILES } from '@shared/domain/campaign/castProfiles';
import { getSpeciesSlug } from './species';
import { IMAGE_CACHE_VERSION } from './petHelpers';

const TRAINERS_BASE = '/campaign/trainers';

/** Stable id for the player's own Keeper portrait avatar option. */
export const PLAYER_PORTRAIT_ID = 'player';

/** Evolution stage used for cast species-image fallbacks (matches the cast gallery). */
const CAST_PORTRAIT_STAGE = 1;

/**
 * Rival IDs (name slugs) that have a bespoke trainer portrait committed to
 * `/public/campaign/trainers/<id>.png`. Keep in sync with the files on disk —
 * anything not listed here uses the species-image fallback.
 */
const TRAINER_PORTRAIT_IDS: ReadonlySet<string> = new Set([
  'timmy',
  'mimi',
  'lulu',
  'vera',
  'cliff',
  'aggie',
  'old-man-gray',
  // Clan Z — the recurring human villains (user-supplied busts). See
  // docs/superpowers/specs/2026-07-12-clan-z-recurring-villains-design.md.
  'zip',
  'zap',
  'zeck-the-boss',
  // Phase 2 — one-off HUMAN rivals from the story worlds. Bust only (they are
  // not cast-gallery members, so no `-full` art is ever requested for them).
  // The rest of the Phase-2 roster (Wardens, gates, locks, embers, tides and
  // the abstract manifestations) are creature-spirits in canon, so the
  // species-image fallback above is the CORRECT art for them, not a stand-in.
  'nameless-kid',
  'lost-mapper',
  'lost-pilgrim',
  'quiet-singer',
  'old-keepers',
  'first-trainer',
  'first-guard',
  // Phase 2 — the mirror / shadow bosses. Each is drawn FROM `player.png` so it
  // echoes the player's own silhouette (beanie, hair, jacket) rather than being
  // a new person; `mirror-timmy` echoes `timmy.png` the same way.
  'mirror-timmy',
  'mirror-keeper',
  'mirror-champ',
  'blank-face',
  'your-shadow',
  'other-you',
  'pale-twin',
]);

/** The player's own trainer avatar ("the Keeper"). */
export const PLAYER_PORTRAIT_SRC = `${TRAINERS_BASE}/player.png?v=${IMAGE_CACHE_VERSION}`;

function trainerSrc(rivalId: string): string {
  return `${TRAINERS_BASE}/${rivalId}.png?v=${IMAGE_CACHE_VERSION}`;
}

/** Stage-accurate species image — the fallback for rivals without trainer art. */
function speciesSrc(speciesId: number, stage: number): string {
  return `/species/${getSpeciesSlug(speciesId)}-evolution${stage}.png?v=${IMAGE_CACHE_VERSION}`;
}

/** Trainer portrait for a rival name, or null if none has been made yet. */
export function trainerPortraitForName(name: string): string | null {
  const id = rivalIdForName(name);
  return TRAINER_PORTRAIT_IDS.has(id) ? trainerSrc(id) : null;
}

/** Full-body (`-full`) trainer art for a name, or null when no bespoke bust exists. */
export function trainerFullBodyForName(name: string): string | null {
  const id = rivalIdForName(name);
  return TRAINER_PORTRAIT_IDS.has(id)
    ? `${TRAINERS_BASE}/${id}-full.png?v=${IMAGE_CACHE_VERSION}`
    : null;
}

/**
 * Portrait for a campaign node's rival: bespoke trainer art when we have it,
 * otherwise the stage-accurate species image of the rival's featured Suikamon.
 */
export function rivalPortraitSrc(node: CampaignNode): string {
  const id = node.rivalId || rivalIdForName(node.rivalName);
  if (TRAINER_PORTRAIT_IDS.has(id)) return trainerSrc(id);
  const stage = node.squad.find((p) => p.speciesId === node.portraitSpeciesId)?.stage ?? 1;
  return speciesSrc(node.portraitSpeciesId, stage);
}

/** A campaign portrait offered as a selectable profile avatar. */
export interface CampaignPortraitOption {
  /** Stable id persisted with the avatar (player id or cast-name slug). */
  id: string;
  /** Human-readable label for the picker. */
  label: string;
  /** Resolved image src (bespoke trainer art, or the species-image fallback). */
  src: string;
}

/**
 * Resolve a campaign portrait image by its stable id, or null if the id is
 * unknown. Cast rivals without bespoke art resolve to their species-image
 * fallback — the exact same rule as {@link trainerPortraitForName}.
 */
export function campaignPortraitSrcById(id: string): string | null {
  if (id === PLAYER_PORTRAIT_ID) return PLAYER_PORTRAIT_SRC;
  const member = CAMPAIGN_CAST_PROFILES.find((m) => rivalIdForName(m.name) === id);
  if (!member) return null;
  return trainerPortraitForName(member.name) ?? speciesSrc(member.portraitSpeciesId, CAST_PORTRAIT_STAGE);
}

/**
 * Every campaign portrait selectable as a profile avatar: the player's Keeper
 * plus every recurring cast member, enumerated from CAMPAIGN_CAST_PROFILES
 * (never hardcoded). Rivals without bespoke art use the species-image fallback.
 */
export const CAMPAIGN_PORTRAIT_OPTIONS: readonly CampaignPortraitOption[] = [
  { id: PLAYER_PORTRAIT_ID, label: 'You', src: PLAYER_PORTRAIT_SRC },
  ...CAMPAIGN_CAST_PROFILES.map((member) => ({
    id: rivalIdForName(member.name),
    label: member.name,
    src: trainerPortraitForName(member.name) ?? speciesSrc(member.portraitSpeciesId, CAST_PORTRAIT_STAGE),
  })),
];
